import { z } from 'zod';
import { regions } from './dimensions/regions.js';
import { industries } from './dimensions/industries.js';

const canonicalRegionSlugs = new Set(regions.map(({ slug }) => slug));
const canonicalRegionLabels = new Map(
  regions.map(({ slug, name }) => [slug, name] as const),
);

/**
 * Exact canonical region-slug validator backed only by the region dimension.
 *
 * Deliberately performs no trimming, case-folding, display-name lookup, or
 * ISO-code conversion: wire producers and consumers must agree on the stored
 * `user_preferences.default_regions` values byte-for-byte.
 */
export const canonicalRegionSlugSchema = z
  .string()
  .min(1)
  .refine((value) => canonicalRegionSlugs.has(value), {
    message: 'must be a canonical region slug',
  });

/** Resolve an exact canonical region slug to its user-facing label. */
export function canonicalRegionLabel(value: string): string | undefined {
  return canonicalRegionLabels.get(value);
}

const canonicalIndustrySlugs = new Set(industries.map(({ slug }) => slug));

/**
 * Exact industry-slug validator backed only by the industry dimension — the
 * closed set `leak_site_claims.victim_sector` is written from (the pipeline's
 * `normaliseSector` returns one of these slugs or null). KAN-805: the burst
 * alert's `sector` reaches an email subject and body, so a value outside this
 * set is refused rather than rendered. Same exactness as the region validator:
 * no trimming, case-folding or display-name lookup.
 */
export const canonicalIndustrySlugSchema = z
  .string()
  .min(1)
  .refine((value) => canonicalIndustrySlugs.has(value), {
    message: 'must be a canonical industry slug',
  });
